import { Currency } from "../utils/Currency";
import { Decimal } from "decimal.js";
import { BalanceModifications } from "./Amortization/BalanceModifications";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isBetween from "dayjs/plugin/isBetween";
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(isBetween);
export class AmortizationExport {
    _amortization;
    constructor(amortization) {
        this.amortization = amortization;
    }
    get amortization() {
        return this._amortization;
    }
    set amortization(value) {
        this._amortization = value;
    }
    exportRepaymentScheduleToCSV() {
        // Step 1: Collect all unique metadata keys
        const metadataKeys = new Set();
        this.amortization.repaymentSchedule.entries.forEach((entry) => {
            Object.keys(entry.metadata).forEach((key) => {
                metadataKeys.add(key);
            });
        });
        // Convert the Set to an Array for easier handling
        const metadataKeysArray = Array.from(metadataKeys);
        // Step 2: Define the fields array, including metadata fields
        const fields = [
            {
                header: "Term",
                value: (entry) => entry.term,
            },
            {
                header: "Billing Model",
                value: (entry) => entry.billingModel || '',
            },
            {
                header: "Period Start Date",
                value: (entry) => entry.periodStartDate.toString(),
            },
            {
                header: "Period End Date",
                value: (entry) => entry.periodEndDate.toString(),
            },
            {
                header: "Bill Open Date",
                value: (entry) => entry.periodBillOpenDate.toString(),
            },
            {
                header: "Bill Due Date",
                value: (entry) => entry.periodBillDueDate.toString(),
            },
            {
                header: "Period Interest Rate",
                value: (entry) => entry.periodInterestRate.toString(),
            },
            {
                header: "Principal",
                value: (entry) => entry.principal.getRoundedValue(this.amortization.roundingPrecision),
            },
            {
                header: "Fees",
                value: (entry) => entry.fees.getRoundedValue(this.amortization.roundingPrecision),
            },
            {
                header: "Billed Deferred Fees",
                value: (entry) => entry.billedDeferredFees.getRoundedValue(this.amortization.roundingPrecision),
            },
            {
                header: "Unbilled Total Deferred Fees",
                value: (entry) => entry.unbilledTotalDeferredFees.getRoundedValue(this.amortization.roundingPrecision),
            },
            {
                header: "Due Interest For Term",
                value: (entry) => entry.dueInterestForTerm.getRoundedValue(this.amortization.roundingPrecision),
            },
            {
                header: "Accrued Interest For Period",
                value: (entry) => entry.accruedInterestForPeriod.getRoundedValue(this.amortization.roundingPrecision),
            },
            {
                header: "Billed Deferred Interest",
                value: (entry) => entry.billedDeferredInterest.getRoundedValue(this.amortization.roundingPrecision),
            },
            {
                header: "Billed Interest For Term",
                value: (entry) => entry.billedInterestForTerm.getRoundedValue(this.amortization.roundingPrecision),
            },
            {
                header: "Balance Modification Amount",
                value: (entry) => entry.balanceModificationAmount.getRoundedValue(this.amortization.roundingPrecision),
            },
            {
                header: "End Balance",
                value: (entry) => entry.endBalance.getRoundedValue(this.amortization.roundingPrecision),
            },
            {
                header: "Start Balance",
                value: (entry) => entry.startBalance.getRoundedValue(this.amortization.roundingPrecision),
            },
            {
                header: "Total Payment",
                value: (entry) => entry.totalPayment.getRoundedValue(this.amortization.roundingPrecision),
            },
            {
                header: "Per Diem",
                value: (entry) => entry.perDiem.getRoundedValue(this.amortization.roundingPrecision),
            },
            {
                header: "Days In Period",
                value: (entry) => entry.daysInPeriod,
            },
            {
                header: "Unbilled Total Deferred Interest",
                value: (entry) => entry.unbilledTotalDeferredInterest.getRoundedValue(this.amortization.roundingPrecision),
            },
            {
                header: "Interest Rounding Error",
                value: (entry) => entry.interestRoundingError.getRoundedValue(this.amortization.roundingPrecision),
            },
            {
                header: "Unbilled Interest Due To Rounding",
                value: (entry) => entry.unbilledInterestDueToRounding.getRoundedValue(this.amortization.roundingPrecision),
            },
            // DSI fields
            {
                header: "DSI Previous Payment Date",
                value: (entry) => entry.dsiPreviousPaymentDate?.toString() || '',
            },
            {
                header: "DSI Interest Days",
                value: (entry) => entry.dsiInterestDays || '',
            },
            {
                header: "DSI Actual Start Balance",
                value: (entry) => entry.actualDSIStartBalance?.getRoundedValue(this.amortization.roundingPrecision) || '',
            },
            {
                header: "DSI Actual End Balance",
                value: (entry) => entry.actualDSIEndBalance?.getRoundedValue(this.amortization.roundingPrecision) || '',
            },
            {
                header: "DSI Actual Interest",
                value: (entry) => entry.actualDSIInterest?.getRoundedValue(this.amortization.roundingPrecision) || '',
            },
            {
                header: "DSI Actual Principal",
                value: (entry) => entry.actualDSIPrincipal?.getRoundedValue(this.amortization.roundingPrecision) || '',
            },
            {
                header: "DSI Actual Fees",
                value: (entry) => entry.actualDSIFees?.getRoundedValue(this.amortization.roundingPrecision) || '',
            },
            {
                header: "DSI Interest Savings",
                value: (entry) => entry.dsiInterestSavings || '',
            },
            {
                header: "DSI Interest Penalty",
                value: (entry) => entry.dsiInterestPenalty || '',
            },
            // Re-amortization fields
            {
                header: "Re-amortized Start Balance",
                value: (entry) => entry.reAmortizedStartBalance?.getRoundedValue(this.amortization.roundingPrecision) || '',
            },
            {
                header: "Re-amortized End Balance",
                value: (entry) => entry.reAmortizedEndBalance?.getRoundedValue(this.amortization.roundingPrecision) || '',
            },
            {
                header: "Re-amortized Interest",
                value: (entry) => entry.reAmortizedInterest?.getRoundedValue(this.amortization.roundingPrecision) || '',
            },
            {
                header: "Re-amortized Principal",
                value: (entry) => entry.reAmortizedPrincipal?.getRoundedValue(this.amortization.roundingPrecision) || '',
            },
            {
                header: "Re-amortized Fees",
                value: (entry) => entry.reAmortizedFees?.getRoundedValue(this.amortization.roundingPrecision) || '',
            },
            {
                header: "Re-amortized Total Payment",
                value: (entry) => entry.reAmortizedTotalPayment?.getRoundedValue(this.amortization.roundingPrecision) || '',
            },
            {
                header: "Re-amortized DSI Interest Days",
                value: (entry) => entry.reAmortizedDsiInterestDays || '',
            },
            {
                header: "Re-amortized Per Diem",
                value: (entry) => entry.reAmortizedPerDiem?.getRoundedValue(this.amortization.roundingPrecision) || '',
            },
            // Status flags
            {
                header: "Is Current Active Term",
                value: (entry) => entry.isCurrentActiveTerm ? 'Yes' : 'No',
            },
            {
                header: "Is Delinquent",
                value: (entry) => entry.isDelinquent ? 'Yes' : 'No',
            },
            {
                header: "Last Payment Date",
                value: (entry) => entry.lastPaymentDate?.toString() || '',
            },
            // Other fields
            {
                header: "Billable Period",
                value: (entry) => entry.billablePeriod ? 'Yes' : 'No',
            },
            {
                header: "Pre-bill Days",
                value: (entry) => entry.prebillDaysConfiguration || '',
            },
            {
                header: "Bill Due Days After Period End",
                value: (entry) => entry.billDueDaysAfterPeriodEndConfiguration || '',
            },
            {
                header: "Calendar Type",
                value: (entry) => entry.calendar?.calendarType || '',
            },
            // Step 3: Add metadata fields dynamically
            ...metadataKeysArray.map((key) => ({
                header: `Metadata.${key}`,
                value: (entry) => {
                    const value = entry.metadata[key];
                    // Handle different types of metadata values
                    if (typeof value === "object" && value !== null) {
                        return JSON.stringify(value);
                    }
                    return value !== undefined ? value : "";
                },
            })),
        ];
        // Helper function to escape CSV fields
        const escapeCSVField = (field) => {
            let str = String(field);
            if (str.includes('"')) {
                str = str.replace(/"/g, '""');
            }
            if (str.includes(",") || str.includes("\n") || str.includes('"')) {
                str = `"${str}"`;
            }
            return str;
        };
        // Generate the header row
        const headerRow = fields.map((field) => field.header).join(",");
        // Generate the data rows
        const dataRows = this.amortization.repaymentSchedule.entries.map((entry) => {
            const row = fields.map((field) => escapeCSVField(field.value(entry)));
            return row.join(",");
        });
        // Combine the header and data rows
        const csvContent = [headerRow, ...dataRows].join("\n");
        return csvContent;
    }
    /**
     * Generates TypeScript code to recreate this Amortization instance.
     * @returns A string containing the TypeScript code.
     */
    toCode() {
        // Helper functions to serialize special types
        const serializeCurrency = (currency) => {
            if (currency instanceof Currency) {
                return `Currency.of(${currency.toNumber()})`;
            }
            else {
                return `Currency.of(${currency})`;
            }
        };
        const serializeDecimal = (decimal) => {
            if (decimal instanceof Decimal) {
                return `new Decimal(${decimal.toString()})`;
            }
            else {
                return `new Decimal(${decimal})`;
            }
        };
        const serializeDayjs = (date) => {
            const dateStr = dayjs.isDayjs(date) ? date.toString() : dayjs(date).toString();
            return `dayjs('${dateStr}')`;
        };
        const serializeAny = (value) => {
            return JSON.stringify(value);
        };
        // Serialize arrays of special types
        const serializeArray = (arr, serializer) => {
            return `[${arr.map(serializer).join(", ")}]`;
        };
        // Serialize the input parameters
        const serializeParams = (params) => {
            const lines = [];
            for (let [key, value] of Object.entries(params)) {
                if (value === undefined || value === null) {
                    continue; // Skip undefined or null values
                }
                let serializedValue;
                switch (key) {
                    case "loanAmount":
                    case "originationFee":
                    case "flushThreshold":
                        serializedValue = serializeCurrency(value);
                        break;
                    case "annualInterestRate":
                        serializedValue = serializeDecimal(value);
                        break;
                    case "startDate":
                    case "endDate":
                    case "firstPaymentDate":
                        serializedValue = serializeDayjs(value);
                        break;
                    case "calendarType":
                        serializedValue = `CalendarType.${value}`;
                        break;
                    case "roundingMethod":
                        serializedValue = `RoundingMethod.${value}`;
                        break;
                    case "termPeriodDefinition":
                        serializedValue = `{
    unit: '${value.unit}',
    count: ${serializeArray(value.count, (item) => item.toString())}
  }`;
                        break;
                    case "balanceModifications":
                        if (!(value instanceof BalanceModifications)) {
                            value = new BalanceModifications(value);
                        }
                        serializedValue = value.toCode();
                        break;
                    // Handle other complex types as needed
                    default:
                        serializedValue = serializeAny(value);
                }
                lines.push(`  ${key}: ${serializedValue},`);
            }
            return lines.join("\n");
        };
        // Build the complete code string
        const code = `import { Amortization, AmortizationParams, CalendarType, RoundingMethod, Fee, TermPeriodDefinition } from './Amortization';
import { BalanceModification } from './BalanceModification';
import { Currency } from '../utils/Currency';
import Decimal from 'decimal.js';
import dayjs from 'dayjs';

// Define the parameters for the loan
const params: AmortizationParams = {
${serializeParams(this.amortization.getInputParams())}
};

// Create the Amortization instance
const amortization = new Amortization(params);
`;
        return code.trim();
    }
    toTestCode() {
        return `
describe('Amortization Class', () => {
  let amortization: Amortization;

  beforeEach(() => {
    ${this.toCode()}
  });

  it('should initialize with correct parameters', () => {
    expect(amortization.loanAmount).toBeDefined();
    expect(amortization.annualInterestRate).toBeDefined();
    expect(amortization.term).toBeGreaterThan(0);
  });

  it('should calculate APR correctly', () => {
    const apr = amortization.apr;
    expect(apr).toBeInstanceOf(Decimal);
    expect(apr.greaterThan(0)).toBe(true);
  });

  it('should generate an amortization schedule', () => {
    const schedule = amortization.generateSchedule();
    expect(schedule.length).toBeGreaterThan(0);
    expect(schedule[0].principal).toBeDefined();
    expect(schedule[0].interestRoundingError).toBeDefined();
  });
});
  `.trim();
    }
}
